import React, { useCallback } from 'react';

import { cssMap } from '@atlaskit/css';
import { Flex, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

import { PlaybackSpeedControl } from './PlaybackSpeedControl';
import { PlayFromBeginningButton } from './PlayFromBeginningButton';
import { PlayPauseButton } from './PlayPauseButton';
import { SkipBackButton } from './SkipBackButton';
import { SkipForwardButton } from './SkipForwardButton';

const gridStyles = cssMap({
	root: {
		gridTemplateColumns: '1fr auto 1fr',
		paddingTop: token('space.050'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
	},
});

export const MiniplayerControls = (): React.JSX.Element => {
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isLoading = streamingState.state === AudioStreamingState.LOADING;
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;
	const { pendingContent } = useAudioPlayerStore();
	const { trackAIResultAction } = useAIEventsInstrumentation();

	const handlePlayFromBeginningButtonClick = useCallback(() => {
		trackAIResultAction('audioPlayFromBeginning');
	}, [trackAIResultAction]);

	const handleSkipBackButtonClick = useCallback(() => {
		trackAIResultAction('audioSkipBackward');
	}, [trackAIResultAction]);

	const handleSkipForwardButtonClick = useCallback(() => {
		trackAIResultAction('audioSkipForward');
	}, [trackAIResultAction]);

	const handlePlaybackSpeedControlClick = useCallback(
		(speed: number) => {
			trackAIResultAction('audioPlaybackSpeed', {
				attributes: {
					playbackSpeed: speed,
				},
			});
		},
		[trackAIResultAction],
	);

	return (
		<Grid testId="miniplayer-controls" gap="space.300" xcss={gridStyles.root} alignItems="center">
			<Flex justifyContent="end" direction="row" gap="space.200">
				{!pendingContent && !isUninitialized && (
					<>
						<PlayFromBeginningButton
							isDisabled={isLoading}
							onClick={handlePlayFromBeginningButtonClick}
						/>
						<SkipBackButton isDisabled={isLoading} onClick={handleSkipBackButtonClick} />
					</>
				)}
			</Flex>
			<PlayPauseButton />
			<Flex direction="row" gap="space.200">
				{!pendingContent && !isUninitialized && (
					<>
						<SkipForwardButton isDisabled={isLoading} onClick={handleSkipForwardButtonClick} />
						<PlaybackSpeedControl onClick={handlePlaybackSpeedControlClick} />
					</>
				)}
			</Flex>
		</Grid>
	);
};
